import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  deleteCustomAddress,
  getCustomAddress,
  listCustomAddresses,
  upsertCustomAddress,
} from "../services/customAddressService";
import { useCustomAddressStore } from "../stores/customAddressStore";
import type { ReqDeleteCustomAddress, ReqUpsertCustomAddress } from "../types/customAddress.types";

const LIST_KEY = "customAddresses";
const DETAIL_KEY = "customAddress";

export const useCustomAddressList = (keyword: string) =>
  useQuery({
    queryKey: [LIST_KEY, keyword],
    queryFn: () => listCustomAddresses(keyword),
    staleTime: 0,
    refetchOnMount: "always",
  });

export const useCustomAddressDetail = (caIdx: number | null) =>
  useQuery({
    queryKey: [DETAIL_KEY, caIdx],
    queryFn: () => getCustomAddress(caIdx!),
    enabled: caIdx != null,
    staleTime: 0,
    gcTime: 0,
  });

export const useUpsertCustomAddress = () => {
  const qc = useQueryClient();
  const closeDrawer = useCustomAddressStore((s) => s.closeDrawer);
  return useMutation({
    mutationFn: (req: ReqUpsertCustomAddress) => upsertCustomAddress(req),
    onSuccess: () => {
      toast.success("보완 주소가 저장되었습니다. 주소 검색에 즉시 반영됩니다.");
      qc.invalidateQueries({ queryKey: [LIST_KEY] });
      qc.invalidateQueries({ queryKey: [DETAIL_KEY] });
      closeDrawer();
    },
    onError: (err: Error) => {
      toast.error(`저장 실패: ${err.message}`);
    },
  });
};

export const useDeleteCustomAddress = () => {
  const qc = useQueryClient();
  const closeDrawer = useCustomAddressStore((s) => s.closeDrawer);
  return useMutation({
    mutationFn: (req: ReqDeleteCustomAddress) => deleteCustomAddress(req),
    onSuccess: () => {
      toast.success("보완 주소가 삭제되었습니다.");
      qc.invalidateQueries({ queryKey: [LIST_KEY] });
      qc.invalidateQueries({ queryKey: [DETAIL_KEY] });
      closeDrawer();
    },
    onError: (err: Error) => {
      toast.error(`삭제 실패: ${err.message}`);
    },
  });
};
